import { GrantDto } from '../../../../dtos/GrantDto';
import { PhaseDto } from '../../../../dtos/PhaseDto';
import { StepDto } from '../../../../dtos/StepDto';
import { GrantActivityItemDto } from '../../../../dtos/GrantActivityItemDto';
import { getProgressTimelineData } from '../../../../api/v2/ProgressTimelineApi';
import { MODULE_CATALOG, ProgressModule } from './moduleCatalog';

// Where a phase's bar runs once its document cycle has been merged: a phase the work moved past
// leaves no gap, and the phase after it starts where the skipped one would have. The server works
// this out in PhaseSpanMerger, shared with the Risk Meter and the Dashboard; phases
// that take no part in merging have no entry here and draw from their own dates.
export interface MergedSpan {
    start: Date | null;
    end: Date | null;
    collapsed: boolean;
}

export interface RawProgressData {
    grantId: string;
    modules: ProgressModule[];
    phasesByModule: Map<string, PhaseDto[]>;
    stepsByPhaseId: Map<string, StepDto[]>;
    mergedSpanByPhaseId: Map<string, MergedSpan>;
    activity: GrantActivityItemDto[];
}

export const loadCrossModuleData = async (grantData: GrantDto): Promise<RawProgressData> => {
    const result = await getProgressTimelineData(grantData.id);
    const data = result?.data;

    // Include every catalog module that has phases on this grant — regardless of whether
    // grantData.modules marks it as "enabled". Otherwise, activity tied to a phase whose
    // module is disabled gets silently dropped (its phase never makes it into the tracked set).
    const moduleIdsWithPhases = new Set<number>(
        (data?.phases ?? [])
            .map(e => e.phase.defaultPhase?.moduleTypeId)
            .filter((id): id is number => id !== undefined && id !== null)
    );
    const enabledIds = new Set((grantData.modules ?? []).filter(m => m.enabled).map(m => m.moduleId));
    const relevantIds = new Set<number>([...enabledIds, ...moduleIdsWithPhases]);
    const modules = MODULE_CATALOG.filter(m => relevantIds.has(m.moduleId));

    const phasesByModule = new Map<string, PhaseDto[]>();
    const stepsByPhaseId = new Map<string, StepDto[]>();
    const mergedSpanByPhaseId = new Map<string, MergedSpan>();
    modules.forEach(m => phasesByModule.set(m.key, []));

    (data?.phases ?? []).forEach(entry => {
        const moduleId = entry.phase.defaultPhase?.moduleTypeId;
        const module = modules.find(m => m.moduleId === moduleId);
        if (module) {
            phasesByModule.get(module.key)!.push(entry.phase);
        }
        stepsByPhaseId.set(entry.phase.id, entry.steps ?? []);

        // A phase outside a document cycle has no merged span and keeps its own dates.
        if (entry.mergedStart || entry.mergedEnd || entry.collapsed) {
            mergedSpanByPhaseId.set(entry.phase.id, {
                start: entry.mergedStart ? new Date(entry.mergedStart) : null,
                end: entry.mergedEnd ? new Date(entry.mergedEnd) : null,
                collapsed: entry.collapsed === true,
            });
        }
    });

    return {
        grantId: grantData.id,
        modules,
        phasesByModule,
        stepsByPhaseId,
        mergedSpanByPhaseId,
        activity: data?.activity ?? [],
    };
};
